import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "monthDisplay", "grid"]
  static values = {
    currentMonth: { type: Number, default: new Date().getMonth() },
    currentYear: { type: Number, default: new Date().getFullYear() },
    availability: { type: Object, default: {} }
  }

  connect() {
    this.loadAvailability()
  }

  async loadAvailability() {
    const startDate = new Date(this.currentYearValue, this.currentMonthValue, 1)
    const endDate = new Date(this.currentYearValue, this.currentMonthValue + 2, 0)

    // Show loading state
    if (this.hasGridTarget) {
      this.gridTarget.innerHTML = '<div class="col-span-7 text-center py-8 text-slate-500">Loading calendar...</div>'
    }

    try {
      const response = await fetch(
        `/appointment_request/calendar_availability?start_date=${this.formatDate(startDate)}&end_date=${this.formatDate(endDate)}`,
        { headers: { 'Accept': 'application/json' } }
      )
      const data = await response.json()
      this.availabilityValue = data
      this.renderCalendar()
    } catch (error) {
      console.error("Error loading availability:", error)
      this.renderCalendar()
    }
  }

  renderCalendar() {
    if (!this.hasGridTarget) return

    const year = this.currentYearValue
    const month = this.currentMonthValue
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startingDay = firstDay.getDay()
    const totalDays = lastDay.getDate()

    // Update month display
    if (this.hasMonthDisplayTarget) {
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ]
      this.monthDisplayTarget.textContent = `${monthNames[month]} ${year}`
    }

    // Build calendar grid
    let html = ""
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Empty cells before first day
    for (let i = 0; i < startingDay; i++) {
      html += '<div class="p-2"></div>'
    }

    // Days of the month
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day)
      const dateStr = this.formatDate(date)
      const isPast = date < today
      const availability = this.availabilityValue[dateStr]
      const isAvailable = availability && availability.available && !isPast

      // Check which time slots are available
      const hasMorning = availability?.slots?.some(slot => slot.key === 'morning' && slot.available)
      const hasAfternoon = availability?.slots?.some(slot => slot.key === 'afternoon' && slot.available)

      let classes = "relative text-center rounded-lg cursor-pointer transition "
      let innerContent = ""

      if (isPast) {
        classes += "text-slate-300 cursor-not-allowed p-2"
        innerContent = `${day}`
      } else if (isAvailable) {
        classes += "text-slate-700 hover:ring-2 hover:ring-emerald-500"

        // Build the split background indicator
        let bgStyle = ""
        if (hasMorning && hasAfternoon) {
          // Both slots available - full green background
          bgStyle = "background: linear-gradient(90deg, #10b981 50%, #10b981 50%);"
        } else if (hasMorning) {
          // Only morning available - left half green
          bgStyle = "background: linear-gradient(90deg, #10b981 50%, #f1f5f9 50%);"
        } else if (hasAfternoon) {
          // Only afternoon available - right half green
          bgStyle = "background: linear-gradient(90deg, #f1f5f9 50%, #10b981 50%);"
        } else {
          // Fallback: if marked available but no slot info, show full green
          bgStyle = "background: linear-gradient(90deg, #10b981 50%, #10b981 50%);"
        }

        innerContent = `
          <div class="absolute inset-0 rounded-lg" style="${bgStyle}"></div>
          <div class="relative z-10 p-2 font-semibold">${day}</div>
        `
      } else {
        classes += "bg-slate-100 text-slate-400 cursor-not-allowed p-2"
        innerContent = `${day}`
      }

      const clickAction = isAvailable ? `data-action="click->quote-wizard#selectDate" data-date="${dateStr}"` : ""

      html += `<div class="${classes}" ${clickAction}>${innerContent}</div>`
    }

    this.gridTarget.innerHTML = html
  }

  prevMonth(event) {
    event.preventDefault()

    if (this.currentMonthValue === 0) {
      this.currentMonthValue = 11
      this.currentYearValue--
    } else {
      this.currentMonthValue--
    }

    this.loadAvailability()
  }

  nextMonth(event) {
    event.preventDefault()

    if (this.currentMonthValue === 11) {
      this.currentMonthValue = 0
      this.currentYearValue++
    } else {
      this.currentMonthValue++
    }

    this.loadAvailability()
  }

  formatDate(date) {
    return date.toISOString().split("T")[0]
  }
}
